import React, { useEffect, useState } from 'react';
import { getAdminSiteSettings, updateAdminSiteSettings, getAdminSeo, saveAdminSeo, deleteAdminSeo } from '../../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const errorText = (e) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Kaydedilemedi');

const MeasurementSettings = () => {
  const [form, setForm] = useState({ ga4_measurement_id: '', gsc_verification: '' });
  useEffect(() => { getAdminSiteSettings().then(r => setForm({ ga4_measurement_id: r.data.ga4_measurement_id || '', gsc_verification: r.data.gsc_verification || '' })).catch(() => {}); }, []);
  const save = async () => {
    try { await updateAdminSiteSettings(form); toast.success('Ölçüm ayarları kaydedildi'); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="rounded-2xl bg-white border border-gray-100 p-6 space-y-4" data-testid="measurement-settings">
      <h3 className="text-lg font-bold">Ölçüm Bağlantıları</h3>
      <div>
        <Label>Google Analytics 4 ölçüm kimliği</Label>
        <Input placeholder="G-XXXXXXXXXX" value={form.ga4_measurement_id} onChange={(e) => setForm({ ...form, ga4_measurement_id: e.target.value.trim() })} data-testid="ga4-id-input" />
        <p className="text-xs text-gray-500 mt-1">Google Analytics → Yönetici → Veri akışları → Web akışı içinde bulunur. Kod, yalnızca çerez iznini kabul eden ziyaretçilerde yüklenir.</p>
      </div>
      <div>
        <Label>Search Console doğrulama kodu (HTML etiketi)</Label>
        <Input placeholder='<meta name="google-site-verification" content="..."> veya sadece kod' value={form.gsc_verification} onChange={(e) => setForm({ ...form, gsc_verification: e.target.value })} data-testid="gsc-code-input" />
        <p className="text-xs text-gray-500 mt-1">Önerilen yöntem: Search Console'da "Alan adı" mülkü ekleyip alan adınızın DNS ayarlarına TXT kaydı girmek. HTML etiketi yöntemi de burada desteklenir.</p>
      </div>
      <Button onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="measurement-save">Kaydet</Button>
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
        <p className="font-semibold">Bağlantı bekleyen işlemler (sizin tamamlamanız gerekir)</p>
        <ul className="list-disc ml-5 mt-1 space-y-1">
          <li>Google Analytics 4 hesabı ve ölçüm kimliği oluşturma.</li>
          <li>Search Console'da istanbulturda.com doğrulaması ve <code>https://istanbulturda.com/api/sitemap.xml</code> site haritasının gönderilmesi.</li>
          <li>GA4 ve Search Console raporlarının bu panele çekilmesi için Google servis hesabı yetkisi (2. aşama).</li>
        </ul>
      </div>
    </div>
  );
};

const EMPTY = { path: '', title: '', description: '', noindex: false };

const SeoOverrides = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const load = () => getAdminSeo().then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await saveAdminSeo(form); toast.success('SEO ayarı kaydedildi'); setForm(EMPTY); load(); } catch (e) { toast.error(errorText(e)); }
  };
  const remove = async (path) => {
    try { await deleteAdminSeo(path); toast.success('Silindi, otomatik değerler kullanılacak'); load(); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="rounded-2xl bg-white border border-gray-100 p-6 space-y-4" data-testid="seo-overrides">
      <h3 className="text-lg font-bold">Sayfa SEO Düzenleme</h3>
      <p className="text-sm text-gray-600">Başlık ve açıklamalar sayfa ve tur içeriğinden otomatik üretilir. Belirli bir sayfa için elle değiştirmek isterseniz adresini yazıp kaydedin (ör. <code>/turlar/istanbul</code> veya <code>/turlar/tur-adresi</code>). Google'da sıralama veya indekslenme garantisi yoktur.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label>Sayfa adresi *</Label><Input value={form.path} onChange={(e) => setForm({ ...form, path: e.target.value })} placeholder="/turlar/istanbul" data-testid="seo-path-input" /></div>
        <div><Label>Başlık (en fazla 70 karakter) – {form.title.length}</Label><Input maxLength={70} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="seo-title-input" /></div>
      </div>
      <div><Label>Meta açıklama (en fazla 170 karakter) – {form.description.length}</Label><Textarea maxLength={170} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="seo-description-input" /></div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.noindex} onChange={(e) => setForm({ ...form, noindex: e.target.checked })} data-testid="seo-noindex-checkbox" /> Bu sayfa Google'da gösterilmesin (noindex)</label>
      <Button onClick={save} disabled={!form.path} className="bg-orange-500 hover:bg-orange-600" data-testid="seo-save">Kaydet</Button>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600"><tr><th className="p-2 text-left">Adres</th><th className="p-2 text-left">Başlık</th><th className="p-2 text-left">Açıklama</th><th className="p-2">noindex</th><th className="p-2" /></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="p-3 text-gray-500">Henüz elle düzenlenmiş sayfa yok; tüm sayfalar otomatik değerleri kullanıyor.</td></tr>}
            {items.map(it => (
              <tr key={it.path} className="border-t" data-testid={`seo-row-${it.path}`}>
                <td className="p-2 whitespace-nowrap">{it.path}</td><td className="p-2">{it.title || '–'}</td><td className="p-2 max-w-xs truncate">{it.description || '–'}</td>
                <td className="p-2 text-center">{it.noindex ? 'Evet' : '–'}</td>
                <td className="p-2 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setForm({ ...EMPTY, ...it, title: it.title || '', description: it.description || '' })} className="mr-1">Düzenle</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(it.path)} className="text-red-600">Sil</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function SeoSettings() {
  return (
    <div className="space-y-6" data-testid="seo-settings">
      <MeasurementSettings />
      <SeoOverrides />
    </div>
  );
}
